import classnames from 'classnames';
import React, {useState} from 'react';
import {Link} from 'react-router';
import type {InjectedRouter} from 'react-router';
import withRouter from 'react-router/lib/withRouter';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {AssignmentMenuOptions} from './AssignmentMenuOptions';
import {history} from 'client/history';

import {
  formatDateRange,
  formatDateTime,
  formatEditedDate,
  getDueCopy,
  isPastDue
} from 'client/TeacherDashboard/rowDateCopy';
import type {FrqGradingStatus} from './AssignmentsList.helpers';
import type {StatusSegment} from './constants';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

// Margin-spaced bullet separator — more breathing room than a plain " • " string join.
const withBullets = (parts: string[]): React.ReactNode =>
  parts.map((part, index) => (
    <React.Fragment key={part}>
      {index > 0 && <span className='mx-x1'>•</span>}
      {part}
    </React.Fragment>
  ));

const getSubmissionsCopy = ({
  countOfCompletedStudents,
  countOfIncompleteStudents
}: TeacherAssignmentRowFieldsFragment): string | null => {
  const total = countOfCompletedStudents + countOfIncompleteStudents;
  return total > 0 ? `${countOfCompletedStudents} / ${total} submissions` : null;
};

interface LateAllowedIndicatorProps {
  showText: boolean;
}

const LateAllowedIndicator = ({showText}: LateAllowedIndicatorProps) =>
  showText ? (
    <span className='ml-x0-half shrink-0'>(Late allowed)</span>
  ) : (
    <Icon
      aria-label='Late submissions allowed'
      className='ml-x0-half shrink-0'
      color='tertiary'
      icon='alarm-snooze'
      iconStyle='regular'
    />
  );

const getSubtitle = (
  assignment: TeacherAssignmentRowFieldsFragment,
  status: StatusSegment,
  showExactDueDate: boolean
): React.ReactNode => {
  if (status === 'draft') {
    return `Edited ${formatEditedDate(assignment.updatedAt)}`;
  }
  if (status === 'closed') {
    // Closed rows still show the due-date range across in-scope classes/students.
    const parts = ['Closed'];
    const dueCopy = getDueCopy(assignment, showExactDueDate);
    if (dueCopy) {
      parts.push(dueCopy);
    }
    parts.push(getSubmissionsCopy(assignment) ?? 'No students assigned');
    return withBullets(parts);
  }
  if (status === 'scheduled') {
    const {minStartDate, maxStartDate} = assignment;
    if (!minStartDate) {
      return 'Scheduled';
    }
    return `Scheduled for ${formatDateRange(minStartDate, maxStartDate, formatDateTime)}`;
  }
  // Open: "Due <date> • <completed> / <total> submissions". Counts are
  // (class, student) pairings, not distinct students.
  const parts: string[] = [];
  const dueCopy = getDueCopy(assignment, showExactDueDate);
  if (dueCopy) {
    parts.push(dueCopy);
  }
  const submissionsCopy = getSubmissionsCopy(assignment);
  if (submissionsCopy) {
    parts.push(submissionsCopy);
  }
  if (!parts.length) {
    return 'Open';
  }
  return withBullets(parts);
};

/** Leads the subtitle: the only part of that line that asks for an action. */
const FRQ_STATUS_DISPLAY: Record<FrqGradingStatus, {copy: string; color: 'warning' | 'positive'}> =
  {
    needsGrading: {copy: 'Free Response Needs Grading', color: 'warning'},
    readyToRelease: {copy: 'Ready to release', color: 'warning'},
    released: {copy: 'Grades Released', color: 'positive'}
  };

const STATUS_DISPLAY: Record<
  StatusSegment,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'},
  draft: {color: 'tertiary'}
};

interface Props {
  /** The acting (possibly masqueraded) teacher — scopes reports and gates delete. */
  actorTeacherId: string;
  assignment: TeacherAssignmentRowFieldsFragment;
  /** Adds the classroom dimension to the report link (class-scoped list). */
  classroomId?: string;
  /** How far this row's FRQ grading has got; the list owns whether there is any to report. */
  frqStatus?: FrqGradingStatus | null;
  /** Grade/release affordance for FRQ assignments; the list owns whether to render it (or none). */
  gradeCell?: React.ReactNode;
  /** Passed down to the menu, which gates cross-class "View Student Answers" on it. */
  isFrqGradingEnabled?: boolean;
  /** Leading LMS grade-sync affordance; the list owns whether/which one to render (or none). */
  lmsCell?: React.ReactNode;
  onDeleteComplete: () => void;
  router: InjectedRouter;
  /** The list segment the row was fetched under. */
  status: StatusSegment;
}

const AssignmentRowBase = ({
  actorTeacherId,
  assignment,
  classroomId,
  frqStatus,
  gradeCell,
  isFrqGradingEnabled,
  lmsCell,
  onDeleteComplete,
  router,
  status
}: Props) => {
  const [showExactDueDate, setShowExactDueDate] = useState(false);
  // educatorStatus is only as fresh as the fetch, so past-due is frozen at mount.
  const [loadedAt] = useState(Date.now);

  const isDraft = status === 'draft';
  const editPath = `/assignment-builder/create-assignment/${assignment.id}/settings${
    isDraft ? '/draft' : ''
  }`;
  const reportPath = actorTeacherId
    ? `/reports/teachers/${actorTeacherId}${
        classroomId ? `/classrooms/${classroomId}` : ''
      }/assignments/${assignment.id}/students`
    : null;
  const isCreator = Boolean(actorTeacherId) && assignment.teacherId === actorTeacherId;

  const subtitle = getSubtitle(assignment, status, showExactDueDate);
  // Open past every due date: late submissions keep it open (or, rarely, an undated pairing).
  const isLateAllowed = status === 'open' && isPastDue(assignment, loadedAt);
  const {color: statusColor, icon: statusIcon, iconStyle: statusIconStyle} = STATUS_DISPLAY[status];
  const frqStatusDisplay = frqStatus ? FRQ_STATUS_DISPLAY[frqStatus] : null;

  const goToBuilder = () => history.pushState(null, editPath);

  return (
    // Clicking anywhere on the row opens the builder. The name stays a real <Link> so keyboard/AT users
    // and middle-click/open-in-new-tab still work — the row onClick is a mouse-only convenience on top of
    // it (hence the a11y disable). The action cluster stops propagation so its buttons don't also navigate.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-primary-hover active:background-primary-pressed flex cursor-pointer items-center'
      onClick={goToBuilder}
      onMouseEnter={() => setShowExactDueDate(true)}
      onMouseLeave={() => setShowExactDueDate(false)}
      onFocus={() => setShowExactDueDate(true)}
      onBlur={() => setShowExactDueDate(false)}
    >
      <div
        className={classnames(
          'flex h-x5 w-x5 shrink-0 items-center justify-center rounded-full',
          isDraft ? 'border border-dashed border-border-bold' : 'background-brand'
        )}
      >
        <Icon
          className='text-m'
          color={isDraft ? 'tertiary' : 'primary-inverse-static'}
          icon='file-lines'
          iconStyle='regular'
        />
      </div>
      <div className='min-w-x0 flex grow flex-col'>
        <Link className='text-m content-primary block truncate no-underline' to={editPath}>
          {assignment.name}
        </Link>
        {subtitle || frqStatusDisplay ? (
          <Text as='div' className='flex items-center' color={statusColor} size='s'>
            <span className='truncate'>
              {frqStatusDisplay ? (
                <>
                  <Text as='span' color={frqStatusDisplay.color} size='s'>
                    {frqStatusDisplay.copy}
                  </Text>
                  {subtitle ? <span className='mx-x1'>•</span> : null}
                </>
              ) : null}
              {/* After the FRQ label: leading the line it reads as that label's own icon. */}
              {statusIcon ? (
                <Icon
                  className='mr-x0-half text-xs!'
                  color={statusColor}
                  icon={statusIcon}
                  iconStyle={statusIconStyle}
                />
              ) : null}
              {subtitle}
            </span>
            {isLateAllowed ? <LateAllowedIndicator showText={showExactDueDate} /> : null}
          </Text>
        ) : null}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className='gap-x0-half flex shrink-0 items-center' onClick={(e) => e.stopPropagation()}>
        {gradeCell}
        {lmsCell}
        {!isDraft && reportPath && (
          <IconButton
            className='relative'
            icon='chart-line'
            iconStyle='regular'
            label='View report'
            tooltip='View report'
            onClick={() => history.pushState(null, reportPath)}
            size='s'
          />
        )}
        <IconButton
          className='relative'
          icon='pencil'
          iconStyle='regular'
          label='Edit'
          tooltip='Edit'
          onClick={() => history.pushState(null, editPath)}
          size='s'
        />
        <span className='relative inline-flex'>
          <AssignmentMenuOptions
            assignmentId={assignment.id}
            assignmentName={assignment.name}
            isDraft={isDraft}
            isCreator={isCreator}
            classroomId={classroomId}
            isFrqGradingEnabled={isFrqGradingEnabled}
            reportPath={reportPath}
            updatedAt={assignment.updatedAt}
            onDeleteComplete={onDeleteComplete}
            router={router}
          />
        </span>
      </div>
    </div>
  );
};

// Memoized so the row bails when the list re-renders (a keystroke, or a global store 'change') with its
// props unchanged. Wrapping outside withRouter is safe: withRouter subscribes to the router itself, so
// navigation still re-renders the row despite the memo.
export const AssignmentRow = React.memo(withRouter(AssignmentRowBase));
